import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import apiService from "../services/apiService";

function Courses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: "",
    description: "",
    department: ""
  });
  const [editCourse, setEditCourse] = useState(null);
  const [deleteCourse, setDeleteCourse] = useState(null);
  const [outcomes, setOutcomes] = useState([{ short_name: "", description: "" }]);
  const [editOutcomes, setEditOutcomes] = useState([]);
  const [activeKebab, setActiveKebab] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileUploadError, setFileUploadError] = useState(null);
  const [showAQMSImportModal, setShowAQMSImportModal] = useState(false);
  const [aqmsCourses, setAqmsCourses] = useState([]);
  const [loadingAQMSCourses, setLoadingAQMSCourses] = useState(false);
  const [importingCourse, setImportingCourse] = useState(null);
  const [aqmsImportError, setAqmsImportError] = useState(null);
  const navigate = useNavigate();
  const kebabRef = useRef(null);
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  
  // Course outcomes creation limit
  const outcomesCreationLimit = 30;

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const data = await apiService.getCourses();
        setCourses(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching courses:", err);
        setError(err.message || "Failed to load courses. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();

    const handleClickOutside = (event) => {
      if (kebabRef.current && !kebabRef.current.contains(event.target)) {
        setActiveKebab(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    const fieldName = id.replace("new-course-", "").replace("-input", "");
    setNewCourse({
      ...newCourse,
      [fieldName]: value
    });
  };

  const handleEditInputChange = (e) => {
    const { id, value } = e.target;
    const fieldName = id.replace("edit-course-", "").replace("-input", "");
    setEditCourse({
      ...editCourse,
      [fieldName]: value
    });
  };

  const handleOutcomeChange = (index, field, value) => {
    const updatedOutcomes = [...outcomes];
    updatedOutcomes[index][field] = value;
    setOutcomes(updatedOutcomes);
  };

  const handleEditOutcomeChange = (index, field, value) => {
    const updatedOutcomes = [...editOutcomes];
    updatedOutcomes[index][field] = value;
    setEditOutcomes(updatedOutcomes);
  };

  const addOutcomeRow = () => {
    if (outcomes.length >= outcomesCreationLimit) {
      alert(`You can add a maximum of ${outcomesCreationLimit} outcomes.`);
      return;
    }
    setOutcomes([...outcomes, { short_name: "", description: "" }]);
  };

  const addEditOutcomeRow = () => {
    if (editOutcomes.length >= outcomesCreationLimit) {
      alert(`You can add a maximum of ${outcomesCreationLimit} outcomes.`);
      return;
    }
    setEditOutcomes([...editOutcomes, { short_name: "", description: "" }]);
  };

  const removeOutcomeRow = (index) => {
    const updatedOutcomes = outcomes.filter((_, i) => i !== index);
    setOutcomes(updatedOutcomes);
  };

  const removeEditOutcomeRow = (index) => {
    const updatedOutcomes = editOutcomes.filter((_, i) => i !== index);
    setEditOutcomes(updatedOutcomes);
  };

  const saveNewCourse = async () => {
    if (!newCourse.title || !newCourse.description || !newCourse.department) {
      alert('Please fill out all required fields for course details.');
      return;
    }

    const validOutcomes = outcomes.filter(
      outcome => outcome.short_name.trim() && outcome.description.trim()
    );

    const courseData = {
      title: newCourse.title,
      description: newCourse.description,
      department: newCourse.department,
      outcomes: validOutcomes
    };

    try {
      const data = await apiService.createCourse(courseData);
      setCourses([...courses, data]);
      closeModal();
    } catch (err) {
      console.error("Error creating course:", err);
      alert(err.message || "Failed to create course. Please try again.");
    }
  };

  const saveEditCourse = async () => {
    if (!editCourse.title || !editCourse.description || !editCourse.department) {
      alert('Please fill out all required fields for course details.');
      return;
    }

    const validOutcomes = editOutcomes.filter(
      outcome => outcome.short_name.trim() && outcome.description.trim()
    );

    const courseData = {
      title: editCourse.title,
      description: editCourse.description,
      department: editCourse.department,
      outcomes: validOutcomes
    };

    try {
      await apiService.updateCourse(editCourse.id, courseData);

      const updatedCourses = courses.map(course =>
        course.id === editCourse.id ? {...course, ...courseData} : course
      );
      setCourses(updatedCourses);
      closeEditModal();
    } catch (err) {
      console.error("Error updating course:", err);
      alert(err.message || "Failed to update course. Please try again.");
    }
  };

  const deleteCourseConfirm = async () => {
    if (!deleteCourse) return;

    try {
      await apiService.deleteCourse(deleteCourse.id);
      const updatedCourses = courses.filter(course => course.id !== deleteCourse.id);
      setCourses(updatedCourses);
      closeDeleteModal();
    } catch (err) {
      console.error("Error deleting course:", err);
      alert(err.message || "Failed to delete course. Please try again.");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/json') {
        setFileUploadError('Please select a valid JSON file.');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setFileUploadError(null);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setFileUploadError('Please select a file to upload.');
      return;
    }

    setUploadingFile(true);
    setFileUploadError(null);

    try {
      const data = await apiService.createCourseFromFile(selectedFile);
      setCourses([...courses, data]);
      alert('Course created successfully from file!');
      closeFileUploadModal();
    } catch (err) {
      console.error("Error uploading course file:", err);
      setFileUploadError(err.message || 'Failed to create course from file. Please check the file format and try again.');
    } finally {
      setUploadingFile(false);
    }
  };

  const openModal = () => {
    setNewCourse({ title: "", description: "", department: "" });
    setOutcomes([{ short_name: "", description: "" }]);
    setShowModal(true);
  };

  const openEditModal = (course) => {
    setEditCourse({...course});
    setEditOutcomes(course.outcomes || []);
    if (!course.outcomes || course.outcomes.length === 0) {
      setEditOutcomes([{ short_name: "", description: "" }]);
    }
    setShowEditModal(true);
    setActiveKebab(null);
  };

  const openDeleteModal = (course) => {
    setDeleteCourse(course);
    setShowDeleteModal(true);
    setActiveKebab(null);
  };

  const openFileUploadModal = () => {
    setShowFileUploadModal(true);
    setSelectedFile(null);
    setFileUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditCourse(null);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteCourse(null);
  };

  const closeFileUploadModal = () => {
    setShowFileUploadModal(false);
    setSelectedFile(null);
    setFileUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openAQMSImportModal = async () => {
    setShowAQMSImportModal(true);
    setLoadingAQMSCourses(true);
    setAqmsImportError(null);
    setAqmsCourses([]);
    
    try {
      const data = await apiService.getAQMSCourses();
      setAqmsCourses(data || []);
      if (!data || data.length === 0) {
        setAqmsImportError("No courses available in AQMS for your account.");
      }
    } catch (err) {
      console.error("Error fetching AQMS courses:", err);
      setAqmsImportError(err.message || "Failed to load courses from AQMS. Please try again later.");
      setAqmsCourses([]);
    } finally {
      setLoadingAQMSCourses(false);
    }
  };

  const closeAQMSImportModal = () => {
    setShowAQMSImportModal(false);
    setAqmsCourses([]);
    setAqmsImportError(null);
    setImportingCourse(null);
  };

  const handleImportCourse = async (aqmsCourse) => {
    if (!aqmsCourse || !aqmsCourse.id) {
      alert("Invalid course data");
      return;
    }

    setImportingCourse(aqmsCourse.id);
    setAqmsImportError(null);

    try {
      // Get user email from AuthContext
      const userEmail = user?.email;
      
      const importedCourse = await apiService.importCourseFromAQMS(
        aqmsCourse.id,
        userEmail
      );

      // Add imported course to local state
      setCourses([...courses, importedCourse]);
      
      // Remove from AQMS courses list to prevent duplicate imports
      setAqmsCourses(prev => prev.filter(c => c.id !== aqmsCourse.id));
      
      alert(`Course "${importedCourse.title || aqmsCourse.name || aqmsCourse.courseCode}" imported successfully!`);
      
    } catch (err) {
      console.error("Error importing course:", err);
      const errorMessage = err.message || "Failed to import course. Please try again.";
      setAqmsImportError(errorMessage);
      
      // Check if it's a duplicate error
      if (errorMessage.toLowerCase().includes('already imported')) {
        // Remove from list if already imported
        setAqmsCourses(prev => prev.filter(c => c.id !== aqmsCourse.id));
        alert(`This course has already been imported.`);
      } else {
        alert(errorMessage);
      }
    } finally {
      setImportingCourse(null);
    }
  };

  const toggleKebab = (e, courseId) => {
    e.stopPropagation();
    setActiveKebab(activeKebab === courseId ? null : courseId);
  };

  const handleCardClick = (courseId) => {
    navigate(`/course/${courseId}`);
  };

  const handleMenuItemClick = (e, action, course) => {
    e.stopPropagation();
    if (action === 'edit') {
      openEditModal(course);
    } else if (action === 'delete') {
      openDeleteModal(course);
    } else if (action === 'view') {
      navigate(`/course/${course.id}`);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
        <h1>My Courses</h1>
        <div style={{ display: "flex", gap: "1rem" }}>
          <button className="btn btn-primary add-course-btn" onClick={openModal}>
            <span>+</span> Add New Course
          </button>
          <button className="btn btn-secondary add-course-btn" onClick={openFileUploadModal}>
            <span>📄</span> Add Course From File
          </button>
          <button className="btn btn-secondary add-course-btn" onClick={openAQMSImportModal}>
            <span>📥</span> Import From AQMS
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-indicator">Loading courses...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : courses.length === 0 ? (
        <div className="empty-state">
          <p>You don't have any courses yet. Click "Add New Course" to get started.</p>
        </div>
      ) : (
        <div className="courses-grid">
          {courses.map(course => (
            <div
              key={course.id}
              className="course-card"
              onClick={() => handleCardClick(course.id)}
              role="button"
              tabIndex="0"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleCardClick(course.id);
                }
              }}
            >
              <div
                className="kebab-menu"
                onClick={(e) => toggleKebab(e, course.id)}
                ref={activeKebab === course.id ? kebabRef : null}
              >
                <span>⋮</span>
                <div className={`kebab-menu-content ${activeKebab === course.id ? 'active' : ''}`}>
                  <div className="kebab-menu-item" onClick={(e) => handleMenuItemClick(e, 'view', course)}>View</div>
                  <div className="kebab-menu-item" onClick={(e) => handleMenuItemClick(e, 'edit', course)}>Edit</div>
                  <div className="kebab-menu-item" onClick={(e) => handleMenuItemClick(e, 'delete', course)}>Delete</div>
                </div>
              </div>
              <h3>{course.title}</h3>
              <p>{course.department}</p>
              <p>{course.description}</p>
            </div>
          ))}
        </div>
      )}

      {/* Add Course Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Add New Course</h2>
              <span className="close-btn" onClick={closeModal}>&times;</span>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="new-course-title-input">Course Title *</label>
                <input
                  type="text"
                  id="new-course-title-input"
                  className="form-control"
                  placeholder="e.g., Biology 101"
                  value={newCourse.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="new-course-description-input">Description *</label>
                <textarea
                  id="new-course-description-input"
                  className="form-control"
                  placeholder="Course description..."
                  value={newCourse.description}
                  onChange={handleInputChange}
                  rows="3"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="new-course-department-input">Department *</label>
                <input
                  type="text"
                  id="new-course-department-input"
                  className="form-control"
                  placeholder="e.g., Science"
                  value={newCourse.department}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <hr style={{ margin: "1.5rem 0" }} />
              <h4>Outcomes (Optional)</h4>
              <div id="new-course-outcomes-container">
                {outcomes.map((outcome, index) => (
                  <div key={index} className="outcome-entry">
                    <input
                      type="text"
                      className="form-control outcome-short-name"
                      placeholder="ID"
                      maxLength={5}
                      value={outcome.short_name}
                      onChange={(e) => handleOutcomeChange(index, "short_name", e.target.value)}
                    />
                    <input
                      type="text"
                      className="form-control outcome-description"
                      placeholder="Outcome Description"
                      value={outcome.description}
                      onChange={(e) => handleOutcomeChange(index, "description", e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => removeOutcomeRow(index)}
                    >×</button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-primary btn-add-outcome"
                onClick={addOutcomeRow}
              >+ Add Outcome</button>
              <hr style={{ margin: "1.5rem 0" }} />
              <button className="btn btn-primary" onClick={saveNewCourse}>Save Course</button>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Modal */}
      {showFileUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "600px" }}>
            <div className="modal-header">
              <h2 className="modal-title">Add Course From File</h2>
              <span className="close-btn" onClick={closeFileUploadModal}>&times;</span>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="course-file-input">
                  Upload Course JSON File *
                </label>
                <p style={{ fontSize: "0.9rem", color: "var(--text-light)", marginBottom: "1rem" }}>
                  Please select a JSON file containing course details including title, description, department, and outcomes.
                </p>
                <input
                  type="file"
                  id="course-file-input"
                  ref={fileInputRef}
                  className="form-control"
                  accept=".json"
                  onChange={handleFileSelect}
                  disabled={uploadingFile}
                />
                {selectedFile && (
                  <div style={{ marginTop: "0.75rem", padding: "0.75rem", backgroundColor: "#f0f8ff", borderRadius: "6px", border: "1px solid #2196f3" }}>
                    <p style={{ margin: 0, fontSize: "0.9rem", color: "#1976d2" }}>
                      <strong>Selected file:</strong> {selectedFile.name}
                    </p>
                    <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.85rem", color: "#666" }}>
                      Size: {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                )}
              </div>

              {fileUploadError && (
                <div style={{ padding: "1rem", backgroundColor: "#ffebee", borderRadius: "6px", border: "1px solid #f44336", marginBottom: "1rem" }}>
                  <p style={{ margin: 0, color: "#c62828", fontSize: "0.9rem" }}>
                    <strong>Error:</strong> {fileUploadError}
                  </p>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "1.5rem" }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={closeFileUploadModal}
                  disabled={uploadingFile}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleFileUpload}
                  disabled={!selectedFile || uploadingFile}
                >
                  {uploadingFile ? "Uploading..." : "Upload & Create Course"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Course Modal */}
      {showEditModal && editCourse && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Edit Course</h2>
              <span className="close-btn" onClick={closeEditModal}>&times;</span>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="edit-course-title-input">Course Title *</label>
                <input
                  type="text"
                  id="edit-course-title-input"
                  className="form-control"
                  placeholder="e.g., Biology 101"
                  value={editCourse.title}
                  onChange={handleEditInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="edit-course-description-input">Description *</label>
                <textarea
                  id="edit-course-description-input"
                  className="form-control"
                  placeholder="Course description..."
                  value={editCourse.description}
                  onChange={handleEditInputChange}
                  rows="3"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="edit-course-department-input">Department *</label>
                <input
                  type="text"
                  id="edit-course-department-input"
                  className="form-control"
                  placeholder="e.g., Science"
                  value={editCourse.department}
                  onChange={handleEditInputChange}
                  required
                />
              </div>
              <hr style={{ margin: "1.5rem 0" }} />
              <h4>Outcomes (Optional)</h4>
              <div id="edit-course-outcomes-container">
                {editOutcomes.map((outcome, index) => (
                  <div key={index} className="outcome-entry">
                    <input
                      type="text"
                      className="form-control outcome-short-name"
                      placeholder="ID"
                      maxLength={30}
                      value={outcome.short_name}
                      onChange={(e) => handleEditOutcomeChange(index, "short_name", e.target.value)}
                    />
                    <input
                      type="text"
                      className="form-control outcome-description"
                      placeholder="Outcome Description"
                      value={outcome.description}
                      onChange={(e) => handleEditOutcomeChange(index, "description", e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => removeEditOutcomeRow(index)}
                    >×</button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-primary btn-add-outcome"
                onClick={addEditOutcomeRow}
              >+ Add Outcome</button>
              <hr style={{ margin: "1.5rem 0" }} />
              <button className="btn btn-primary" onClick={saveEditCourse}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Course Modal */}
      {showDeleteModal && deleteCourse && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "500px" }}>
            <div className="modal-header">
              <h2 className="modal-title">Delete Course</h2>
              <span className="close-btn" onClick={closeDeleteModal}>&times;</span>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{deleteCourse.title}</strong>?</p>
              <p>This action cannot be undone. All lessons, assignments, and other content associated with this course will be permanently deleted.</p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "2rem" }}>
                <button className="btn btn-secondary" onClick={closeDeleteModal}>Cancel</button>
                <button className="btn btn-danger" onClick={deleteCourseConfirm}>Delete Course</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AQMS Import Modal */}
      {showAQMSImportModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "800px", maxHeight: "80vh", overflowY: "auto" }}>
            <div className="modal-header">
              <h2 className="modal-title">Import Course From AQMS</h2>
              <span className="close-btn" onClick={closeAQMSImportModal}>&times;</span>
            </div>
            <div className="modal-body">
              {loadingAQMSCourses ? (
                <div className="loading-indicator">Loading courses from AQMS...</div>
              ) : aqmsImportError && aqmsCourses.length === 0 ? (
                <div style={{ padding: "1rem", backgroundColor: "#ffebee", borderRadius: "6px", border: "1px solid #f44336", marginBottom: "1rem" }}>
                  <p style={{ margin: 0, color: "#c62828", fontSize: "0.9rem" }}>
                    <strong>Error:</strong> {aqmsImportError}
                  </p>
                  <button 
                    className="btn btn-primary" 
                    onClick={openAQMSImportModal}
                    style={{ marginTop: "1rem" }}
                  >
                    Retry
                  </button>
                </div>
              ) : aqmsCourses.length === 0 ? (
                <div className="empty-state">
                  <p>No courses available in AQMS for your account.</p>
                </div>
              ) : (
                <>
                  {aqmsImportError && (
                    <div style={{ padding: "1rem", backgroundColor: "#fff3cd", borderRadius: "6px", border: "1px solid #ffc107", marginBottom: "1rem" }}>
                      <p style={{ margin: 0, color: "#856404", fontSize: "0.9rem" }}>
                        <strong>Note:</strong> {aqmsImportError}
                      </p>
                    </div>
                  )}
                  <p style={{ marginBottom: "1rem", fontSize: "0.9rem", color: "var(--text-light)" }}>
                    Select a course from AQMS to import into InPods AI. Imported courses will include all course details from AQMS.
                  </p>
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {aqmsCourses.map((aqmsCourse) => (
                      <div
                        key={aqmsCourse.id}
                        style={{
                          padding: "1rem",
                          marginBottom: "0.75rem",
                          border: "1px solid #e0e0e0",
                          borderRadius: "6px",
                          backgroundColor: "#fafafa",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: "0 0 0.5rem 0", fontSize: "1rem" }}>
                            {aqmsCourse.name || aqmsCourse.courseCode || `Course ${aqmsCourse.id}`}
                          </h4>
                          {aqmsCourse.courseCode && (
                            <p style={{ margin: "0 0 0.25rem 0", fontSize: "0.85rem", color: "var(--text-light)" }}>
                              Code: {aqmsCourse.courseCode}
                            </p>
                          )}
                          {aqmsCourse.description && (
                            <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.85rem", color: "var(--text-light)" }}>
                              {aqmsCourse.description.length > 100 
                                ? `${aqmsCourse.description.substring(0, 100)}...` 
                                : aqmsCourse.description}
                            </p>
                          )}
                        </div>
                        <button
                          className="btn btn-primary"
                          onClick={() => handleImportCourse(aqmsCourse)}
                          disabled={importingCourse === aqmsCourse.id}
                          style={{ marginLeft: "1rem", minWidth: "120px" }}
                        >
                          {importingCourse === aqmsCourse.id ? "Importing..." : "Import Course"}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "1.5rem" }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={closeAQMSImportModal}
                  disabled={loadingAQMSCourses || importingCourse !== null}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Courses;
